import { FC, useState, useEffect, useContext, useRef, useCallback } from "react";
import cx from "classnames";
import { NotificationContext } from "@app/notifications/NotificationContext";
import type Message from "@app/notifications/message";
import { MessageType } from "@app/notifications";
import {
  AiChatProgressNotificationMessagePayload,
} from "@app/notifications/message";

const TRANSITION_DURATION_MS = 700;

// Reassurance messages shown when the workflow goes quiet (no new progress update arrives). Each
// fires at its delay measured from the last received update, phrased to describe the sort of work
// the pipeline is most likely doing by that point.
const STALL_STATUSES: { afterMs: number; text: string }[] = [
  { afterMs: 10000, text: "Still working..." },
  { afterMs: 15000, text: "Finishing up..." },
];

const INITIAL_STATUS = "Understanding your question...";

interface AIChatLoadingStatusesProps {
  activeThreadId: string;
}

const AIChatLoadingStatuses: FC<AIChatLoadingStatusesProps> = ({ activeThreadId }) => {
  const notificationContext = useContext(NotificationContext);
  const [displayedStatus, setDisplayedStatus] = useState<string>(INITIAL_STATUS);
  const [isStatusVisible, setIsStatusVisible] = useState(true);
  const displayedStatusRef = useRef<string>(INITIAL_STATUS);
  const fadeOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every received progress update; resets the stall-escalation schedule below.
  const [lastUpdateSeq, setLastUpdateSeq] = useState(0);
  const [activeStatus, setActiveStatus] = useState<0 | 1 | 2>(0);

  // Cross-fade the visible status line to a new message (no-op if it is already showing).
  const transitionTo = useCallback((next: string) => {
    if (displayedStatusRef.current === next) {
      return;
    }
    setIsStatusVisible(false);
    if (fadeOutTimerRef.current !== null) {
      clearTimeout(fadeOutTimerRef.current);
    }
    fadeOutTimerRef.current = setTimeout(() => {
      displayedStatusRef.current = next;
      setDisplayedStatus(next);
      setIsStatusVisible(true);
      fadeOutTimerRef.current = null;
    }, TRANSITION_DURATION_MS);
  }, []);

  // Clear any pending fade timer on unmount.
  useEffect(() => () => {
    if (fadeOutTimerRef.current !== null) {
      clearTimeout(fadeOutTimerRef.current);
    }
  }, []);

  // Fallback: when SignalR is unavailable, step through the three static status lines on fixed
  // intervals. Runs only without notificationContext — when context is present the stall-escalation
  // effect above already drives status transitions, making these timers redundant. Both timer IDs
  // are tracked so they can be cleared on unmount or if notificationContext appears mid-session.
  useEffect(() => {
    if (notificationContext) {
      return;
    }
    let innerTimer: ReturnType<typeof setTimeout>;
    const outerTimer = setTimeout(() => {
      setActiveStatus(1);
      innerTimer = setTimeout(() => {
        setActiveStatus(2);
      }, 5000);
    }, 3000);
    return () => {
      clearTimeout(outerTimer);
      clearTimeout(innerTimer);
    };
  }, [notificationContext]);

  useEffect(() => {
    if (!notificationContext) {
      return;
    }
    const handler = (message: Message) => {
      if (message.notificationType === MessageType.AiChatProgress && message.payload) {
        const payload = message.payload as AiChatProgressNotificationMessagePayload;
        // Ignore progress for a different conversation (e.g. another tab, or a stale prior request).
        if (payload.threadId !== activeThreadId) {
          return;
        }
        transitionTo(payload.status);
        setLastUpdateSeq(seq => seq + 1);
      }
    };
    notificationContext.subscribe(handler);
    return () => notificationContext.unsubscribe(handler);
  }, [notificationContext, activeThreadId, transitionTo]);

  // Escalate to progressively more descriptive "still working" messages when no new update has
  // arrived for 5/10/15s. Re-scheduled from scratch whenever a real update lands (lastUpdateSeq).
  useEffect(() => {
    if (!notificationContext) {
      return;
    }
    const timers = STALL_STATUSES.map(({ afterMs, text }) => setTimeout(() => transitionTo(text), afterMs));
    return () => timers.forEach(clearTimeout);
  }, [notificationContext, lastUpdateSeq, transitionTo]);

  if (notificationContext) {
    return (
      <div className="ai-chat-loading-statuses__container">
        <p className={cx({
          "ai-chat-loading-statuses__status": true,
          "ai-chat-loading-statuses__visible": isStatusVisible,
        })}>{displayedStatus}</p>
      </div>
    );
  }

  return (
    <div className="ai-chat-loading-statuses__container">
      <p className={cx({
        "ai-chat-loading-statuses__status": true,
        "ai-chat-loading-statuses__visible": activeStatus === 0
      })}>Understanding your question...</p>
      <p className={cx({
        "ai-chat-loading-statuses__status": true,
        "ai-chat-loading-statuses__visible": activeStatus === 1
      })}>Retrieving your data...</p>
      <p className={cx({
        "ai-chat-loading-statuses__status": true,
        "ai-chat-loading-statuses__visible": activeStatus === 2
      })}>Summarizing your data...</p>
    </div>
  );
}

export { AIChatLoadingStatuses };
